import * as rxjs from "rxjs";

export const FPS_60 = 1000 / 60;
export const FPS_30 = 1000 / 30;

export interface TickInfo {
  tick: number;
  t: number;
  dt: number;
}

export interface ClockOptions {
  interval?: number;
  paused?: boolean;
  scheduler?: rxjs.SchedulerLike;
  now?: () => number;
}

export interface ClockChange {
  kind: "paused" | "resumed" | "interval";
  interval: number;
}

function assertInterval(ms: number) {
  if (!Number.isFinite(ms) || ms <= 0) throw new RangeError(`invalid tick interval: ${ms}`);
}

export class DispatchClock {
  readonly scheduler: rxjs.SchedulerLike;
  readonly #now: () => number;

  readonly #interval$: rxjs.BehaviorSubject<number>;
  readonly #paused$: rxjs.BehaviorSubject<boolean>;
  readonly #change$ = new rxjs.Subject<ClockChange>();
  readonly #disposed$ = new rxjs.Subject<void>();
  #last: TickInfo;
  #anchor: number;
  #disposed = false;

  readonly change$: rxjs.Observable<ClockChange> = this.#change$.asObservable();
  readonly tick$: rxjs.Observable<TickInfo>;

  constructor(options: ClockOptions = {}) {
    const interval = options.interval ?? FPS_30;
    assertInterval(interval);

    this.scheduler = options.scheduler ?? rxjs.asyncScheduler;
    this.#now = options.now ?? (() => this.scheduler.now());
    this.#interval$ = new rxjs.BehaviorSubject(interval);
    this.#paused$ = new rxjs.BehaviorSubject(options.paused ?? false);
    this.#anchor = this.now();
    this.#last = { tick: 0, t: this.#anchor, dt: 0 };

    this.tick$ = rxjs.combineLatest([this.#paused$, this.#interval$]).pipe(
      rxjs.switchMap(([paused, ms]) =>
        rxjs.defer(() => {
          this.#anchor = this.now();
          return paused ? rxjs.NEVER : rxjs.interval(ms, this.scheduler);
        })
      ),
      rxjs.map(() => this.#advance()),
      rxjs.takeUntil(this.#disposed$),
      rxjs.share(),
    );
  }

  get tick(): number {
    return this.#last.tick;
  }

  get last(): TickInfo {
    return this.#last;
  }

  get interval(): number {
    return this.#interval$.value;
  }

  set interval(ms: number) {
    this.setInterval(ms);
  }

  get paused(): boolean {
    return this.#paused$.value;
  }

  now(): number {
    return this.#now();
  }

  setInterval(ms: number) {
    assertInterval(ms);
    if (this.#disposed || ms === this.#interval$.value) return;
    this.#interval$.next(ms);
    this.#change$.next({ kind: "interval", interval: ms });
  }

  pause() {
    if (this.#disposed || this.#paused$.value) return;
    this.#paused$.next(true);
    this.#change$.next({ kind: "paused", interval: this.interval });
  }

  resume() {
    if (this.#disposed || !this.#paused$.value) return;
    this.#paused$.next(false);
    this.#change$.next({ kind: "resumed", interval: this.interval });
  }

  toggle() {
    if (this.paused) this.resume();
    else this.pause();
  }

  dispose() {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#disposed$.next();
    this.#disposed$.complete();
    this.#change$.complete();
    this.#paused$.complete();
    this.#interval$.complete();
  }

  #advance(): TickInfo {
    const t = this.now();
    this.#last = { tick: this.#last.tick + 1, t, dt: t - this.#anchor };
    this.#anchor = t;
    return this.#last;
  }
}
